import React, { useEffect, useState } from 'react';
import { errorToast, successToast } from '../../utils/helper';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import ToggleSwitch from '../../component/common/ToggleSwitch';
import { Api } from '../../api';
import { CompanyApi } from '../../api/companyApi';
import {
  getUsersCustomOfferings,
  modifyUsersCustomOffering,
  getCompanyUsersCustomOfferings,
  modifyCompanyUsersCustomOffering
} from '../../api/offeringApi';
import { DEPARTMENT } from '../../utils/constants';

const AddUsersModal = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  isB2C = false,
  offeringId,
  isAdmin,
  slug
}) => {
  const [loader, setLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]); // Users to be added
  const [removedUsers, setRemovedUsers] = useState([]); // Users to be removed
  const [searchTerm, setSearchTerm] = useState('');

  // Department states
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [departmentLoading, setDepartmentLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    console.log('isB2C value in AddUsersModal:', isB2C, companyId);
    if (isOpen) {
      // Don't fetch departments for B2C users
      if (!isB2C) {
        fetchDepartments();
      }
      setPage(1);
      setHasMore(true);
      fetchUsersByDepartment(1, true);
    }
  }, [isOpen, companyId]);

  // Debounce user search
  useEffect(() => {
    if (searchTerm !== undefined && offeringId) {
      const delayDebounceFn = setTimeout(() => {
        setPage(1);
        setHasMore(true);
        fetchUsersByDepartment(1, true);
      }, 800);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  // Fetch users when department changes
  useEffect(() => {
    if (offeringId && selectedDepartment !== undefined) {
      setPage(1);
      setHasMore(true);
      fetchUsersByDepartment(1, true);
    }
  }, [selectedDepartment]);

  const fetchDepartments = async () => {
    setDepartmentLoading(true);
    setDepartments([]);
    try {
      // Use appropriate API based on isAdmin flag
      const apiFunction = isAdmin ? Api.getDepartmentList : CompanyApi.getCompanyDepartmentList;
      const response = await apiFunction(companyId);
      console.log('Departments response:', response);
      if (response?.data?.meta?.code === 1) {
        const list = response.data?.data.map((obj) => ({
          ...obj,
          name: obj.department_name,
          value: obj.department_name
        }));

        // Combine with DEPARTMENT constant and remove "Select"
        const combined = [...list, ...DEPARTMENT].filter((item) => item.name !== 'Select');

        // Sort alphabetically, keeping "Others" at the end
        const sortedList = combined.sort((a, b) => {
          if (a.name === 'Others') return 1;
          if (b.name === 'Others') return -1;
          return a.name.localeCompare(b.name);
        });

        setDepartments(sortedList);
        // Default to "All" (empty string)
        setSelectedDepartment('');
      } else {
        setDepartments([]);
        setSelectedDepartment('');
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      errorToast('Failed to fetch departments');
      setDepartments([]);
      setSelectedDepartment('');
    } finally {
      setDepartmentLoading(false);
    }
  };

  const fetchUsersByDepartment = async (pageNum = 1, resetList = false) => {
    if (!offeringId) return;

    // Prevent multiple simultaneous requests
    if (loadingMore && !resetList) return;

    // Don't fetch if no more data
    if (!hasMore && !resetList) return;

    if (resetList) {
      setLoader(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = {
        search: searchTerm,
        page: pageNum,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      // Add isB2C parameter only for SuperAdmin

      if (isB2C && isAdmin) {
        params.isB2C = 'true';
      }

      // Only add department if not "All" (empty string) and not B2C
      if (selectedDepartment && !isB2C) {
        params.department = selectedDepartment;
      }

      const apiFunction = isAdmin ? getUsersCustomOfferings : getCompanyUsersCustomOfferings;
      const response = await apiFunction(offeringId, params);

      if (response?.data) {
        const newUsers = response.data;

        if (resetList) {
          setUsers(newUsers);
        } else {
          // Append new users to existing list
          setUsers((prevUsers) => [...prevUsers, ...newUsers]);
        }

        // Check if there are more users to load
        if (newUsers.length < 10) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        setPage(pageNum);
      } else {
        errorToast('Failed to fetch users');
        if (resetList) {
          setUsers([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      errorToast('Failed to fetch users');
      if (resetList) {
        setUsers([]);
      }
      setHasMore(false);
    } finally {
      setLoader(false);
      setLoadingMore(false);
    }
  };

  // Handle scroll event for infinite loading
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Check if user has scrolled to bottom (with 10px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 10) {
      if (hasMore && !loadingMore) {
        fetchUsersByDepartment(page + 1, false);
      }
    }
  };

  const handleUserToggle = (user) => {
    const userId = user.id;

    if (user.havePlan) {
      // User already has perk - toggling will remove them
      setRemovedUsers((prev) => {
        if (prev.includes(userId)) {
          return prev.filter((id) => id !== userId);
        } else {
          return [...prev, userId];
        }
      });
      setAddedUsers((prev) => prev.filter((id) => id !== userId));
    } else {
      // User doesn't have perk - toggling will add them
      setAddedUsers((prev) => {
        if (prev.includes(userId)) {
          return prev.filter((id) => id !== userId);
        } else {
          return [...prev, userId];
        }
      });
      setRemovedUsers((prev) => prev.filter((id) => id !== userId));
    }

    // Update selectedUsers for UI state
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    const allUsersWithPlan = users.filter((user) => user.havePlan).length;
    const currentlySelected = users.filter((user) => {
      const userId = user.id;
      if (user.havePlan) {
        return !removedUsers.includes(userId);
      } else {
        return addedUsers.includes(userId);
      }
    }).length;

    if (currentlySelected === users.length) {
      // Deselect All
      const usersToRemove = [];

      users.forEach((user) => {
        const userId = user.id;
        if (user.havePlan) {
          // If user has plan, add to removed list
          usersToRemove.push(userId);
        }
      });

      setRemovedUsers(usersToRemove);
      setAddedUsers([]); // Clear all added users
      setSelectedUsers([]);
    } else {
      // Select All
      const usersToAdd = [];

      users.forEach((user) => {
        const userId = user.id;
        if (!user.havePlan) {
          // Only add users who don't have plan
          usersToAdd.push(userId);
        }
      });

      setAddedUsers(usersToAdd);
      setRemovedUsers([]); // Clear removed users

      // Set selected users for UI (all users)
      const allUserIds = users.map((user) => user.id);
      setSelectedUsers(allUserIds);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!offeringId) {
      errorToast('Missing company or perk information');
      return;
    }

    if (addedUsers.length === 0 && removedUsers.length === 0) {
      errorToast('Please make at least one change');
      return;
    }

    setLoader(true);
    try {
      const reqBody = {
        offeringId: offeringId,
        userIds: addedUsers.map((userId) => userId.toString()),
        removeUserIds: removedUsers.map((userId) => userId.toString())
      };

      const apiFunction = isAdmin ? modifyUsersCustomOffering : modifyCompanyUsersCustomOffering;
      const response = await apiFunction(offeringId, reqBody);

      console.log('Submit data:', reqBody);

      const enrolled = addedUsers.length;
      const removed = removedUsers.length;
      let message = '';

      if (enrolled > 0 && removed > 0) {
        message = `Successfully enrolled ${enrolled} user(s) and removed ${removed} user(s)`;
      } else if (enrolled > 0) {
        message = `Successfully enrolled ${enrolled} user(s)`;
      } else if (removed > 0) {
        message = `Successfully removed ${removed} user(s)`;
      }

      successToast(message);
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error updating users:', error);
      errorToast('Failed to update users');
    } finally {
      setLoader(false);
    }
  };

  const handleClose = () => {
    setUsers([]);
    setSelectedUsers([]);
    setAddedUsers([]);
    setRemovedUsers([]);
    setSearchTerm('');
    setSelectedDepartment('');
    setPage(1);
    setHasMore(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {loader && <Loader />}
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose}></div>

      {/* Modal */}
      <div className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">
            Add Users to Perk
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Department Selection - Hidden for B2C */}
            {!isB2C && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Department
                </label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => {
                    setSelectedDepartment(e.target.value);
                    setUsers([]);
                    setAddedUsers([]);
                    setRemovedUsers([]);
                    setSelectedUsers([]);
                    setSearchTerm('');
                    setPage(1);
                    setHasMore(true);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-shoorah-primary dark:bg-shoorah-darkBg dark:text-white"
                  disabled={departmentLoading}
                >
                  <option value="">All Departments</option>
                  {departments?.map((dept) => (
                    <option key={dept._id || dept.value} value={dept.value}>
                      {dept.name.charAt(0).toUpperCase() + dept.name.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* User Search */}
            {
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Users
                </label>
                <SearchInput
                  id="searchUsers"
                  name="searchUsers"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                />
              </div>
            }

            {/* Users List */}
            {users.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select Users <span className="text-red-500">*</span>
                    <span className="text-gray-500 font-normal ml-2">
                      (
                      {
                        users.filter((user) => {
                          const userId = user.id;
                          if (user.havePlan) {
                            return !removedUsers.includes(userId);
                          } else {
                            return addedUsers.includes(userId);
                          }
                        }).length
                      }{' '}
                      selected)
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-shoorah-primary hover:text-shoorah-secondary"
                  >
                    {users.filter((user) => {
                      const userId = user.id;
                      if (user.havePlan) {
                        return !removedUsers.includes(userId);
                      } else {
                        return addedUsers.includes(userId);
                      }
                    }).length === users.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </button>
                </div>

                <div
                  className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto"
                  onScroll={handleScroll}
                >
                  {users.map((user) => {
                    const userId = user.id;
                    let isToggled = user.havePlan;

                    if (user.havePlan) {
                      isToggled = !removedUsers.includes(userId);
                    } else {
                      isToggled = addedUsers.includes(userId);
                    }

                    return (
                      <div
                        key={userId}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-shoorah-darkBorder last:border-b-0"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                        </div>
                        <ToggleSwitch
                          toggleValue={isToggled}
                          setToggleValue={() => handleUserToggle(user)}
                          disabled={false}
                        />
                      </div>
                    );
                  })}

                  {/* Loading More Indicator */}
                  {loadingMore && (
                    <div className="flex items-center justify-center p-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-shoorah-primary"></div>
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        Loading more users...
                      </span>
                    </div>
                  )}

                  {/* No More Data Indicator */}
                  {!hasMore && users.length > 0 && (
                    <div className="text-center py-3 text-sm text-gray-500 dark:text-gray-400">
                      No more users to load
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* No Users Message */}
            {users.length === 0 && !loader && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No users found in this department.</p>
                <p className="text-sm mt-1">
                  Try selecting a different department or adjust your search.
                </p>
              </div>
            )}

            {/* Loading Message */}
            {departmentLoading && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>Loading departments...</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addedUsers.length === 0 && removedUsers.length === 0}
              className="btn-primary flex-1"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUsersModal;
